import {
  IconName,
  ROLE,
  removeEmptyFilters,
  useAssetsFilter,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  usePositionAssetTypesFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isEqual, startCase } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { isUserRole, useFeatureFlag, useUser } from '../../../../hooks';
import { useDerivativeExpiriesFilter } from '../../../../hooks/filters/useDerivativeExpiriesFilter';
import { useMarketAccountGroupsFilter } from '../../../../hooks/filters/useMarketAccountGroupsFilter';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import type { PositionsTableFilter } from '../types';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';
import type { PositionsSourceFilterMode } from './unifiedSourceFilter';
import { getUnifiedSourceFilterOptions, normalizePersistedSourceFilter } from './unifiedSourceFilter';

interface UsePositionsUnifiedFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
}

export const usePositionsUnifiedFilter = ({ defaultFilter, ...params }: UsePositionsUnifiedFilterParams) => {
  const user = useUser();
  const { enablePredictionMarkets } = useFeatureFlag();
  const baseFilter = usePositionsBaseFilter(params);
  const assetsFilter = useAssetsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const expiriesFilter = useDerivativeExpiriesFilter();
  const positionAssetTypesFilter = usePositionAssetTypesFilter({ includePredictionMarkets: enablePredictionMarkets });
  const accountsFilter = useMarketAccountsFilter();
  const marketAccountGroupsFilter = useMarketAccountGroupsFilter();
  const isTalosAdmin = isUserRole(user, ROLE.TALOS_ADMIN);

  const sourceFilterOptions = useMemo(() => getUnifiedSourceFilterOptions(isTalosAdmin), [isTalosAdmin]);

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        { ...assetsFilter, label: 'Instrument' },
        currencyOfSymbolFilter,
        basePairFilter,
        accountsFilter,
        expiriesFilter,
        positionAssetTypesFilter,
        marketAccountGroupsFilter,
        {
          key: 'Source',
          label: 'Source',
          icon: IconName.CubeTransparent,
          control: 'single-select',
          options: sourceFilterOptions,
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [
      assetsFilter,
      currencyOfSymbolFilter,
      basePairFilter,
      accountsFilter,
      expiriesFilter,
      positionAssetTypesFilter,
      marketAccountGroupsFilter,
      sourceFilterOptions,
    ]
  );

  const { changeFilter, initialFilterClauses } = baseFilter;

  useEffect(() => {
    changeFilter(curr => {
      const normalizedSource = normalizePersistedSourceFilter(curr.Source, isTalosAdmin);
      if (isEqual(curr.Source, normalizedSource)) {
        return curr;
      }
      return { ...curr, Source: normalizedSource };
    });
  }, [changeFilter, isTalosAdmin]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const selectedSource = filterClausesByPropertyKey.get('Source')?.selections?.[0] as
          | PositionsSourceFilterMode
          | undefined;

        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PositionsTableFilter),
          Source: normalizePersistedSourceFilter(selectedSource ?? curr.Source, isTalosAdmin),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, isTalosAdmin]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filterBuilderProps,
    ...baseFilter,
  };
};

const UNIFIED_SERVER_FILTER_KEYS = ['Symbols', 'MarketAccounts', 'SubAccounts'] as const;

export type UnifiedServerFilterQuery = Pick<PositionsTableFilter, (typeof UNIFIED_SERVER_FILTER_KEYS)[number]>;

/**
 * Allow-listed filters shared by POSITION and BALANCE_DELTA.
 * AssetTypes stays client-side so every wsMerge source stays in sync when Product changes.
 */
export function toUnifiedServerFilterQuery(filter: PositionsTableFilter | undefined): UnifiedServerFilterQuery {
  return pickDefinedFilterQuery(filter, UNIFIED_SERVER_FILTER_KEYS);
}

function pickDefinedFilterQuery<K extends keyof PositionsTableFilter>(
  filter: PositionsTableFilter | undefined,
  keys: readonly K[]
): Pick<PositionsTableFilter, K> {
  if (filter == null) {
    return {} as Pick<PositionsTableFilter, K>;
  }

  const query = {} as Pick<PositionsTableFilter, K>;
  for (const key of keys) {
    const value = filter[key];
    if (value != null) {
      query[key] = value;
    }
  }
  return query;
}
